/**
 * Product Details Page Component
 * 
 * Main component exported for use in layout apps.
 * This is the composable micro-frontend component.
 */

'use client';

import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useAddToCart } from '@/lib/hooks/use-product';
import { useProductPage } from '@/lib/hooks/use-product-page';
import { useCartContext } from '@/lib/context/cart-context';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { ProductDetails } from '../ProductDetails';
import { WhyCustomersLoveIt } from '../WhyCustomersLoveIt';
import { ClinicalResults } from '../ClinicalResults';
import { BeforeAfterResults } from '../BeforeAfterResults';
import { KeyIngredients } from '../KeyIngredients';
import { HowToUse } from '../HowToUse';
import { ExpertFormulated } from '../ExpertFormulated';
import { FAQAccordion } from '../FAQAccordion';
import { RiskFreeGuarantee } from '../RiskFreeGuarantee';
import { Breadcrumbs } from '@/components/molecules/Breadcrumbs';
import { Accordion, Icon } from '@template-project/afrodita-components';
import { ReviewsSection } from '../ReviewsSection';
import { RecommendedProducts } from '../RecommendedProducts';
import { useFunnelContext } from '@template-project/storefront-funnel';
import { storefrontLayoutLogger } from '@/lib/logger';
import { currencyRatioFromAmounts } from '@/domain/value-objects/currency-amount.vo';

const PageContainer = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
`;

const BreadcrumbsWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.md};
`;

const Section = styled.section`
  width: 100%;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space['2xl']} ${theme.space.md}`};
`;


export interface ProductDetailsPageProps {
  productSlug: string;
  countryCode?: string;
  currency?: string;
  vendor?: string;
  shop?: string;
}

export function ProductDetailsPage({
  productSlug,
  countryCode: countryCodeOverride,
  currency: currencyOverride,
  vendor = 'template-project',
  shop = 'template-project',
}: ProductDetailsPageProps) {
  const { countryConfig, localizedPath } = useIntl();
  const countryCode = countryCodeOverride || countryConfig.countryCode;
  const currency = currencyOverride || countryConfig.currency.code;
  const { data, isLoading, error } = useProductPage({
    slug: productSlug,
    currency,
    country: countryCode,
  });
  const product = data?.product;
  const addToCartMutation = useAddToCart();
  const { cartToken, setCartToken, showCart } = useCartContext();
  const { t } = useTranslation('pdp');
  const { assignedFunnelId, activeExperience } = useFunnelContext();
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>();
  const persistedFunnelId = assignedFunnelId ?? activeExperience?.funnelId ?? null;

  const handleFunnelAddToCart = useFunnelAddToCart({
    countryCode,
    currency,
    vendor,
    shop,
  });

  // Set default variant when product loads
  useEffect(() => {
    if (product && !selectedVariantId) {
      const defaultVariant = product.variants.find(v => v.enabled) || product.variants[0];
      if (defaultVariant) {
        setSelectedVariantId(defaultVariant.id);
      }
    }
  }, [product, selectedVariantId]);

  const handleAddToCart = async (variantId: string, quantity: number) => {
    const variant = product!.variants.find((v) => v.id === variantId);
    const unitPrice = variant?.price ?? product!.price;
    const currencyRatio =
      data?.pricing?.money?.currency_ratio ??
      (unitPrice.display > 0 ? currencyRatioFromAmounts(unitPrice.base, unitPrice.display) : 1);

    try {
      const result = await addToCartMutation.mutateAsync({
        cartToken: cartToken || undefined,
        productId: product!.uuid,
        variantId,
        quantity,
        options: {
          countryCode,
          currency,
          currencyRatio,
          vendor,
          shop,
          ...(persistedFunnelId ? { upsellFunnelId: persistedFunnelId } : {}),
        },
      });

      if (result.cartToken) {
        setCartToken(result.cartToken);
      }
      showCart();
    } catch (error) {
      storefrontLayoutLogger.error('Failed to add product to cart from ProductDetailsPage', error instanceof Error ? error : undefined, {
        productId: product?.uuid ?? null,
        variantId,
        quantity,
      });
      throw error;
    }
  };

  if (isLoading) {
    return <div>{t('product.loading')}</div>;
  }

  if (error) {
    return <div>{t('product.error')}: {error.message}</div>;
  }

  if (!product) {
    return <div>{t('product.notFound')}</div>;
  }

  const breadcrumbItems = [
    { label: t('sections.home'), href: localizedPath('/') },
    { label: t('sections.products'), href: localizedPath('/products') },
    { label: product.title },
  ];

  return (
    <PageContainer>
      {/* FunnelExperience moved to ProductDetailPage wrapper to avoid unmount/remount on product load */}

      {/* Breadcrumbs */}
      <BreadcrumbsWrapper>
        <Breadcrumbs items={breadcrumbItems} />
      </BreadcrumbsWrapper>

      {/* Main Product Details Section */}
      <Section>
        <ProductDetails
          product={product}
          pricing={data?.pricing}
          selectedVariantId={selectedVariantId}
          onVariantChange={setSelectedVariantId}
          onAddToCart={handleAddToCart}
        />
      </Section>

      {/* Product Description Accordion - "Why you'll love it!" */}
      {product.description_html && (
        <ContentSection>
          <Accordion
            variant="simple"
            openIcon={Icon.PDP.Plus}
            closeIcon={Icon.PDP.Minus}
            defaultOpen={true}
            header={
              <h6 style={{ 
                margin: 0, 
                fontSize: '14px', 
                fontWeight: 600,
                color: 'inherit'
              }}>
                {t('sections.whyLoveIt')}
              </h6>
            }
            content={product.description_html}
            innerHTML={true}
          />
        </ContentSection>
      )}

      {/* Why Customers Love It */}
      {product.benefits && product.benefits.length > 0 && (
        <Section>
            <WhyCustomersLoveIt
              benefits={product.benefits}
              image="https://via.placeholder.com/600x500"
              imageAlt="Product benefits"
              title={t('sections.whyCustomersLove')}
            />
          </Section>
      )}

      {/* Before/After Results */}
      {product.before_after_results && product.before_after_results.length > 0 && (
        <Section>
          <BeforeAfterResults
            results={product.before_after_results}
            subtitle="Reduced lines appearance + Brighter look & Improved Hydration"
            title={t('sections.results')}
          />
        </Section>
      )}

      {/* Clinical Results */}
      {product.clinical_results && product.clinical_results.length > 0 && (
        <Section>
          <ClinicalResults
            clinicalResults={product.clinical_results}
            defaultOpened
            disclaimer={product.clinical_results_disclaimer}
            title={t('sections.clinicalResults')}
          />
        </Section>
      )}

      {/* Key Ingredients */}
      {product.ingredients && product.ingredients.length > 0 && (
        <Section>
          <KeyIngredients
            ingredients={product.ingredients}
            title={t('sections.keyIngredients')}
          />
        </Section>
      )}

      {/* How To Use */}
      {product.how_to_use && product.how_to_use.length > 0 && (
        <Section>
          <HowToUse
            steps={product.how_to_use}
            title={t('sections.howToUse')}
          />
        </Section>
      )}

      {/* Expert Formulated */}
      {product.formulated_by && (
        <Section>
          <ExpertFormulated
            description={product.formulated_by.description}
            image={product.formulated_by.image}
            name={product.formulated_by.name}
            title={product.formulated_by.title}
          />
        </Section>
      )}

      {/* Risk-Free Guarantee */}
      <Section>
        <RiskFreeGuarantee
          titleOverride={product.guarantee_title}
          descriptionOverride={product.guarantee_description}
          iconURL={product.guarantee_icon}
        />
      </Section>

      {/* Reviews Section */}
      <Section>
        <ReviewsSection productId={product.uuid} />
      </Section>

      {/* You May Also Like */}
      <Section>
        <RecommendedProducts productId={product.uuid} />
      </Section>

      {/* FAQ Section */}
      {product.faqs && product.faqs.length > 0 && (
        <Section>
          <FAQAccordion
            faqs={product.faqs}
            title={t('sections.faq')}
          />
        </Section>
      )}
    </PageContainer>
  );
}
